import { Navigate } from '@tanstack/react-router';

import { Image as ImageIcon } from 'lucide-react';

import { Button, cn, FileUpload } from '@src/components/ui';
import { useActiveOrg, usePermissions } from '@src/context/hooks';
import {
  Form,
  SelectField,
  Submit,
  TextField,
} from '@src/corelib/forms/generic';
import { useBusinessSaveMutation, useMineQuery } from '@src/query';

import { Toaster, useToasts } from '../Listings/useToasts';
import { businessDefaults, businessSchema, timezoneOptions } from './businessForm';
import PageHeader from './PageHeader';


function LogoRow({ logo, onUpload, onRemove, busy }) {
  return (
    <div className="flex items-center gap-3.5">
      {logo
        ? (
          <img
            alt=""
            src={logo}
            className="size-14 shrink-0 rounded-lg border border-hairline-strong object-cover"
          />
        )
        : (
          <span
            className={cn(
              'grid size-14 shrink-0 place-items-center rounded-lg border border-hairline-strong bg-surface-2',
            )}
          >
            <ImageIcon className="size-[22px] text-ink-subtle" />
          </span>
        )}

      <FileUpload
        accept="image/*"
        onSelect={(files) => {
          const file = files[0];
          if (file) {
            onUpload(file);
          }
        }}
      >
        {({ open }) => (
          <Button
            type="button"
            variant="secondary"
            className="h-9 px-3.5 text-table"
            loading={busy}
            onClick={open}
          >
            Upload logo
          </Button>
        )}
      </FileUpload>

      {logo
        ? (
          <Button
            type="button"
            variant="ghost"
            disabled={busy}
            className="h-9 px-0 text-table text-ink-subtle hover:bg-transparent hover:text-ink"
            onClick={onRemove}
          >
            Remove
          </Button>
        )
        : null}
    </div>
  );
}

LogoRow.displayName = 'LogoRow';


/** The nine-field business details body. Lives inside `<Form>`; the timezone
 * select reads the seller's current zone so a non-listed zone is preserved. */
function BusinessFields({ currentTimezone }) {
  return (
    <>
      <TextField name="name" label="Business name" />

      <div className="flex flex-col gap-3.5 sm:flex-row">
        <TextField type="email" className="flex-1" name="contact_email" label="Contact email" />
        <TextField className="flex-1" name="contact_phone" label="Contact phone" />
      </div>

      <TextField name="address" label="Street address" />

      <div className="flex flex-col gap-3.5 sm:flex-row">
        <TextField name="city" label="City" className="flex-[2]" />
        <TextField name="region" label="State" className="flex-1" />
        <TextField label="ZIP" name="postal_code" className="flex-1" />
      </div>

      <div className="flex flex-col gap-3.5 sm:flex-row">
        <div className="flex-1">
          <SelectField name="timezone" label="Timezone" options={timezoneOptions(currentTimezone)} />
        </div>

        <TextField
          className="flex-1"
          name="business_hours"
          label="Business hours"
          placeholder="Mon–Fri 9–5"
        />
      </div>

      <div className="flex">
        <Submit className="h-[42px] px-[22px] text-sm">Save changes</Submit>
      </div>
    </>
  );
}

BusinessFields.displayName = 'BusinessFields';


/** Business page (#146, .pen fwDqe): org details — name, contact, address,
 * timezone, hours, logo. Owner/Manager only; other roles are redirected. */
function Business() {
  const { toasts, push } = useToasts();
  // Mine query retained only for its cache key (PATCH invalidates the org);
  // the active seller + role flags come from the org/permission contexts.
  const { queryKey: mineKey } = useMineQuery();
  const { seller } = useActiveOrg();
  const { canManageOrg: canManage } = usePermissions();

  const saveMutation = useBusinessSaveMutation();

  if (!seller) {
    return null;
  }

  if (!canManage) {
    return <Navigate replace to="/app/settings" />;
  }

  const currentTimezone = seller.timezone || 'UTC';

  const save = (values) => saveMutation
    .mutateAsync({ sellerId: seller.id, payload: values, queryKey: mineKey })
    .then(() => push('Business saved'));

  const uploadLogo = (file) => {
    const body = new FormData();
    body.append('logo', file);
    saveMutation.mutate(
      { sellerId: seller.id, payload: body, contentType: 'multipart/form-data', queryKey: mineKey },
      {
        onSuccess: () => push('Logo updated'),
        onError: () => push('Could not upload logo', 'danger'),
      },
    );
  };

  const removeLogo = () => {
    saveMutation.mutate(
      { sellerId: seller.id, payload: { logo: null }, queryKey: mineKey },
      {
        onSuccess: () => push('Logo removed'),
        onError: () => push('Could not remove logo', 'danger'),
      },
    );
  };

  return (
    <div className="flex flex-1 flex-col gap-5 lg:p-7">
      <PageHeader title="Business" subtitle="Company details on invoices & listings" />

      <div className="flex w-full max-w-[640px] flex-col gap-4 px-4 pb-6 lg:px-0">
        <LogoRow
          busy={saveMutation.isPending}
          logo={seller.logo}
          onUpload={uploadLogo}
          onRemove={removeLogo}
        />

        <Form
          schema={businessSchema}
          className="flex flex-col gap-4"
          defaultValues={businessDefaults(seller)}
          onSubmit={save}
          onError={(error, meta) => {
            if (!meta?.isInvalid) {
              push('Could not save business', 'danger');
            }
          }}
        >
          <BusinessFields currentTimezone={currentTimezone} />
        </Form>
      </div>

      <Toaster toasts={toasts} />
    </div>
  );
}

Business.displayName = 'Business';
export default Business;
